import React, { memo } from 'react';
import { MoreHorizontal } from 'lucide-react'; // Keep utility icons
import { ICONS } from '../utils/icons'; // [NEW] 3D Icons

// 1. Mock Data Generator (Mixed Types)
const MOCK_ITEMS = [
    { type: 'card', title: "등록 학생", value: "142", icon: ICONS.student, color: "bg-blue-50" },
    { type: 'list', title: "김하은 (7세)", subtitle: "이번주 결석", icon: ICONS.student, status: "absent" },
    { type: 'card', title: "이번주 출석", value: "98%", icon: ICONS.attendance, color: "bg-green-50" },
    { type: 'chart', title: "헌금 추이", color: "bg-purple-500" },
    { type: 'list', title: "이준호 (6세)", subtitle: "출석 완료", icon: ICONS.Check_mark_button, status: "present" },
    { type: 'card', title: "새가족", value: "+5", icon: ICONS.hearthands, color: "bg-pink-50" },
    { type: 'schedule', title: "여름 성경학교 기획", time: "14:00 PM", color: "bg-orange-50 border-orange-100" },
    { type: 'card', title: "남은 예산", value: "₩1.2M", icon: ICONS.money, color: "bg-purple-50" },
    { type: 'list', title: "박서준 (5세)", subtitle: "생일자", icon: ICONS.Birthday, status: "birthday" },
    { type: 'chart', title: "출석률", color: "bg-green-500" },
    { type: 'card', title: "심방 예정", value: "3명", icon: ICONS.phone, color: "bg-yellow-50" },
    { type: 'schedule', title: "교사 전체 회의", time: "16:30 PM", color: "bg-blue-50 border-blue-100" },
    { type: 'card', title: "설교 노트", value: "New", icon: ICONS.books, color: "bg-indigo-50" },
    { type: 'list', title: "최수민 (7세)", subtitle: "병원 심방", icon: ICONS.Smiling_face_with_hearts, status: "visit" },
    // [NEW] Added Items for More Variety
    { type: 'list', title: "정민우 (5세)", subtitle: "전화 심방", icon: ICONS.phone, status: "visit" },
    { type: 'card', title: "이번달 목표", value: "달성", icon: ICONS.fire, color: "bg-red-50" },
    { type: 'schedule', title: "찬양팀 연습", time: "10:30 AM", color: "bg-teal-50 border-teal-100" },
    { type: 'chart', title: "새가족 유입", color: "bg-blue-500" },
    { type: 'list', title: "김지아 (6세)", subtitle: "등반 완료", icon: ICONS.OK_hand, status: "present" },

    { type: 'list', title: "이도윤 (7세)", subtitle: "결석 (여행)", icon: ICONS.calendar, status: "absent" },
    { type: 'schedule', title: "부서 임원 모임", time: "19:00 PM", color: "bg-indigo-50 border-indigo-100" },
    { type: 'card', title: "간식 준비", value: "완료", icon: ICONS.party, color: "bg-orange-50" },
];

// 2. Component Variations
// [Modified] Added mb-4 for seamless vertical loop (replaces gap)
const MockCard = ({ item }) => (
    <div className="bg-white/95 backdrop-blur-sm p-4 rounded-2xl shadow-sm border border-white/60 flex items-center gap-3 w-48 mx-auto transition-transform duration-300 hover:scale-105 mb-4">
        <div className={`p-2.5 rounded-xl ${item.color}`}>
            <img src={item.icon || ICONS.sparkles} alt="icon" className="w-8 h-8 object-contain" onError={(e) => e.target.src = ICONS.sparkles} />
        </div>
        <div>
            <p className="text-xs text-gray-500 font-bold">{item.title}</p>
            <p className="text-lg font-black text-gray-800 font-do-hyeon">{item.value}</p>
        </div>
    </div>
);

const MockListRow = ({ item }) => (
    <div className="bg-white/95 backdrop-blur-sm px-4 py-3 rounded-xl shadow-sm border border-white/60 flex items-center justify-between w-48 mx-auto transition-transform duration-300 hover:scale-105 mb-4">
        <div className="flex items-center gap-3">
            <img src={item.icon || ICONS.student} alt="avatar" className="w-8 h-8 rounded-full bg-gray-50 object-cover" />
            <div>
                <p className="text-xs font-bold text-gray-700">{item.title}</p>
                <p className={`text-[10px] font-bold ${item.status === 'absent' ? 'text-red-500' :
                    item.status === 'birthday' ? 'text-orange-500' :
                        item.status === 'visit' ? 'text-pink-500' : 'text-green-500'
                    }`}>{item.subtitle}</p>
            </div>
        </div>
        <MoreHorizontal size={14} className="text-gray-300" />
    </div>
);

const MockChart = ({ item }) => (
    <div className="bg-white/95 backdrop-blur-sm p-4 rounded-2xl shadow-sm border border-white/60 w-48 mx-auto transition-transform duration-300 hover:scale-105 mb-4">
        <div className="flex justify-between items-end mb-2">
            <p className="text-xs text-gray-500 font-bold">{item.title}</p>
            <span className="text-[10px] text-green-500 font-bold">▲ 12%</span>
        </div>
        <div className="flex items-end gap-1 h-12">
            <div className={`w-1/5 rounded-t-sm opacity-30 ${item.color}`} style={{ height: '40%' }}></div>
            <div className={`w-1/5 rounded-t-sm opacity-50 ${item.color}`} style={{ height: '70%' }}></div>
            <div className={`w-1/5 rounded-t-sm opacity-40 ${item.color}`} style={{ height: '50%' }}></div>
            <div className={`w-1/5 rounded-t-sm opacity-80 ${item.color}`} style={{ height: '90%' }}></div>
            <div className={`w-1/5 rounded-t-sm ${item.color}`} style={{ height: '65%' }}></div>
        </div>
    </div>
);

const MockSchedule = ({ item }) => (
    <div className={`bg-white/95 backdrop-blur-sm p-3 rounded-xl shadow-sm border-l-4 w-48 mx-auto transition-transform duration-300 hover:scale-105 mb-4 ${item.color}`}>
        <p className="text-[10px] text-gray-500 font-bold mb-0.5">{item.time}</p>
        <p className="text-xs font-bold text-gray-800">{item.title}</p>
    </div>
);

const MockItemRenderer = ({ item }) => {
    switch (item.type) {
        case 'card': return <MockCard item={item} />;
        case 'list': return <MockListRow item={item} />;
        case 'chart': return <MockChart item={item} />;
        case 'schedule': return <MockSchedule item={item} />;
        default: return null;
    }
};

const Column = ({ speed, offset, items }) => (
    <div style={{ transform: `translateY(${offset}px)` }}>
        <div
            className="flex flex-col animate-vertical-scroll"
            style={{
                animationDuration: `${speed}s`,
            }}
        >
            {/* Infinite Scroll Duplication */}
            {[...items, ...items, ...items, ...items].map((item, i) => ( // 4x duplication for safety on taller screens
                <MockItemRenderer key={i} item={item} />
            ))}
        </div>
    </div>
);

const ShowcaseBackground = () => {
    // Shuffle or slice items for variety per column
    const shuffled1 = [...MOCK_ITEMS].sort(() => Math.random() - 0.5);
    const shuffled2 = [...MOCK_ITEMS].sort(() => Math.random() - 0.5);
    const shuffled3 = [...MOCK_ITEMS].sort(() => Math.random() - 0.5);
    const shuffled4 = [...MOCK_ITEMS].sort(() => Math.random() - 0.5);
    const shuffled5 = [...MOCK_ITEMS].sort(() => Math.random() - 0.5);
    // [NEW] Added more columns to fill wide screens
    const shuffled6 = [...MOCK_ITEMS].sort(() => Math.random() - 0.5);
    const shuffled7 = [...MOCK_ITEMS].sort(() => Math.random() - 0.5);
    const shuffled8 = [...MOCK_ITEMS].sort(() => Math.random() - 0.5);

    return (
        <div className="fixed inset-0 overflow-hidden bg-[#F2F0EA] pointer-events-none select-none">
            {/* 3D Container */}
            <div
                className="absolute inset-[-50%] flex justify-center gap-6 opacity-80"
                style={{
                    transform: 'perspective(1000px) rotateX(20deg) rotateY(10deg) rotateZ(-10deg) scale(1.5)',
                    transformStyle: 'preserve-3d'
                }}
            >
                {/* Rolling Columns with varied content */}
                <Column speed={80} offset={0} items={shuffled1} />
                <Column speed={100} offset={-200} items={shuffled2} />
                <Column speed={70} offset={-100} items={shuffled3} />
                <Column speed={90} offset={-300} items={shuffled4} />
                <Column speed={120} offset={-50} items={shuffled5} />
                {/* [NEW] Extra Columns */}
                <Column speed={110} offset={-150} items={shuffled6} />
                <Column speed={85} offset={-250} items={shuffled7} />
                <Column speed={95} offset={-100} items={shuffled8} />
            </div>

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#F8FAFC]/80 via-transparent to-[#F8FAFC]/80"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-[#F8FAFC]/80 via-transparent to-[#F8FAFC]/80"></div>
        </div>
    );
};

export default memo(ShowcaseBackground);
